import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, effect, ElementRef, inject, input, InputSignal, signal, ViewEncapsulation, viewChild, WritableSignal, OnInit, OnDestroy } from '@angular/core';
import { Image, ModalGalleryService, ModalGalleryRef, ModalGalleryConfig, ModalLibConfig } from '@ks89/angular-modal-gallery';
import { TranslateModule } from '@ngx-translate/core';
import { DebugService } from 'src/app/services/debug.service';
import { ShiftScrollService } from 'src/app/services/shift-scroll.service';
import { ToolsService } from 'src/app/services/tools.service';
import Swiper from 'swiper';
import SwiperCore from 'swiper';
import { FreeMode, Pagination, Navigation } from 'swiper';

SwiperCore.use([FreeMode, Pagination, Navigation]);

@Component({
    selector: 'details-hero',
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule
    ],
    templateUrl: './hero.component.html',
    styleUrl: './hero.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})

export class DetailsHeroComponent implements OnInit, OnDestroy {
  public swiperRef = viewChild.required<ElementRef>('swiper');
  
  public items: InputSignal<any[]> = input.required<any[]>();
  public isModal: InputSignal<any> = input<any>(false);
  private modalGalleryService = inject(ModalGalleryService)
  private debugService = inject(DebugService);
  public tools = inject(ToolsService);
  private _shiftScrollService = inject(ShiftScrollService);
  
  public multimedia = signal(null);
  public showSwiper: WritableSignal<boolean> = signal(false);
  public galleryImages: any[] = [];
  
  private _swiperInstance!: Swiper;
  private _shiftScrollElementId?: string;

  setSWiper = effect(() => {
    const swiperEl = this.swiperRef().nativeElement;
    if(!swiperEl || !this.showSwiper()) return;

    const swiperConfig = {
        slidesPerView: "auto" as const,
        spaceBetween: 8,
        pagination: { 
            clickable: true,
            el: '.swiper-pagination'
        },
        navigation: { 
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev'
        },
        breakpoints: { 
            500: {
                slidesPerView: this.items()?.length >= 2 ? 1.1 : 1,
                spaceBetween: 8,
            },
            1000: {
                slidesPerView: this.items()?.length >= 3 ? 2.1 : 2,
                spaceBetween: 8,
            },
        }
    };
    
    this._swiperInstance = new Swiper(swiperEl, swiperConfig);
    
    // Configurar Swiper con Shift+Scroll y guardar el ID del elemento
    this._shiftScrollElementId = this._shiftScrollService.setupSwiperShiftScroll(this._swiperInstance, swiperEl);
  }, { allowSignalWrites: true })

  ngOnInit(){
    this.galleryImages = [];
    let imageCount = 0;

    this.multimedia.set(this.items()?.map(element => {
      if (element.type === 'image'){ 
        const image: Image = new Image(imageCount, { img: element.name });
        element.imageIndex = imageCount;
        this.galleryImages[imageCount] = image;
        imageCount++;
      }
      return element;
    }))

    setTimeout(() => {
        this.showSwiper.set(true);
    }, 750);
  }

  openModal(id: number, imagesArrayToUse: Image[], imageIndex: number, libConfig?: ModalLibConfig): void {
    if (this.tools.isWebApp())
      return;
    
    if(imagesArrayToUse.length === 0) {
        this.debugService.error('[GallerySliderComponent] Images array empty');
        return;
    }
    if(imageIndex > imagesArrayToUse.length - 1) {
        this.debugService.error('[GallerySliderComponent] imageIndex not valid');
        return;
    }
    const imageToShow: Image = imagesArrayToUse[imageIndex];
    const dialogRef: ModalGalleryRef = this.modalGalleryService.open({
        id,
        images: imagesArrayToUse,
        currentImage: imageToShow,
        libConfig
    } as ModalGalleryConfig) as ModalGalleryRef;
  }
  
  ngOnDestroy() {
    // Limpiar elemento específico usando su ID
    if (this._shiftScrollElementId) {
      this._shiftScrollService.cleanupElement(this._shiftScrollElementId);
    }
    
    // Destruir la instancia del Swiper específica de este componente
    this._swiperInstance?.destroy(true, true);
  }
}